// NewAssetMenu — PlayCanvas-style "New asset" submenu.
//
// Used in three places, all routed through the same code path:
//   1. Toolbar [+] button.
//   2. Right-click on a folder in the FolderTree (with extra Rename /
//      New subfolder / Delete items below).
//   3. Right-click on empty space in the AssetGrid (with extra Copy /
//      Paste items below).
//
// The caller passes `extraItems` to append; clicking any of them fires
// onPick(key) with the item's `key`, and the menu dismisses.
//
// Renders a fixed-position floating panel anchored to (x, y) supplied
// by the caller. Outside-click and Escape dismiss.
//
// WHAT it offers is data, in src/lib/newAssetItems.js — including the grouping
// of the Material and Blueprint families into submenus, and the reasoning for
// it. This file is only HOW that is drawn, so the key contract with
// ContentBrowser has tests that need no DOM
// (tests/unit/newAssetItems.test.mjs). Group rows open on hover, the same shape
// the material Apply-to submenu already uses in AssetContextMenu.

import React, { useEffect, useState } from 'react'
import { isOrgEnvironmentsEnabled } from '../../lib/featureFlags'
import { getNewAssetItems } from '../../lib/newAssetItems'
import { useOverlayAnchor } from '../../lib/useOverlayAnchor'
import MenuSubmenu, { isInsideSubmenu } from '../MenuSubmenu'

const C = {
  bg:       '#1a1d28',
  border:   '#2a2c36',
  hover:    '#252938',
  text:     '#e1e1ed',
  dim:      '#8f8fa2',
  primary:  '#bec2ff',
  divider:  '#2a2c36',
}

const W = 240
const SUB_W = 230

export default function NewAssetMenu({ x, y, onPick, onClose, extraItems = [] }) {
  const items = getNewAssetItems({ orgEnvironments: isOrgEnvironmentsEnabled() })
  const [openGroup, setOpenGroup] = useState(null)

  useEffect(() => {
    // The family flyouts are portaled to <body> (MenuSubmenu), so a click
    // on one is not a click on this menu. Without the exemption the menu
    // closes out from under the item being picked.
    const onDocClick = (e) => { if (isInsideSubmenu(e.target)) return; onClose() }
    const onKey = e => { if (e.key === 'Escape') onClose() }
    // Schedule a microtask before listening so the same right-click that
    // opened the menu doesn't immediately close it.
    const tid = setTimeout(() => {
      document.addEventListener('click', onDocClick)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(tid)
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  // Position, clamp, submenu side and the open/press feedback: all from
  // useOverlayAnchor, which is also where the two defects this menu had are
  // fixed for every menu at once — a hand-summed height estimate
  // (36 + n*28 + …) that the regrouping had already invalidated, and a clamp
  // that mixed the event's viewport space with the menu's own zoomed one.
  const { props: anchor, flip } = useOverlayAnchor(x, y, { margin: 4, submenuWidth: SUB_W })

  const pick = (key) => { onPick(key); onClose() }

  return (
    <div
      {...anchor}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => e.preventDefault()}
      // Moving the pointer from a family row onto its flyout now LEAVES this
      // element — the flyout is portaled out. Without the relatedTarget check
      // the group closes on the way to the very list it opened.
      onMouseLeave={(e) => { if (!isInsideSubmenu(e.relatedTarget)) setOpenGroup(null) }}
      style={{
        ...anchor.style, zIndex: 1100,
        background: C.bg, border: `1px solid ${C.border}`,
        borderRadius: 4, padding: 4, minWidth: W,
        boxShadow: '0 8px 24px rgba(0,0,0,.45)',
      }}
    >
      <SectionHeader>New asset</SectionHeader>
      {items.map(item => (item.children ? (
        <GroupItem
          key={item.group}
          item={item}
          open={openGroup === item.group}
          flip={flip}
          onOpen={() => setOpenGroup(item.group)}
          onPick={pick}
        />
      ) : (
        <MenuItem key={item.key} item={item} onClick={() => pick(item.key)} />
      )))}
      {extraItems.length > 0 && <Divider />}
      {extraItems.map(item => (
        <MenuItem
          key={item.key}
          item={item}
          onClick={() => {
            if (item.disabled) return
            onPick(item.key)
            onClose()
          }}
        />
      ))}
    </div>
  )
}

function SectionHeader({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--m-font)', fontSize: 9, letterSpacing: '.12em',
      textTransform: 'uppercase', color: C.dim,
      padding: '6px 10px 4px',
    }}>{children}</div>
  )
}

function Divider() {
  return <div style={{ height: 1, background: C.divider, margin: '4px 6px' }} />
}

// A family row: hovering opens its variants, and the row itself picks nothing.
// Opening on hover (not click) is what the material Apply-to submenu does; a
// plain click here would be a dead click on the one row that has no key, so it
// opens the group too rather than doing nothing.
function GroupItem({ item, open, flip, onOpen, onPick }) {
  const rowRef = React.useRef(null)
  return (
    <div ref={rowRef} onMouseEnter={onOpen} style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); onOpen() }}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', justifyContent: 'space-between',
          background: open ? C.hover : 'transparent',
          border: 'none', color: C.text,
          fontFamily: 'var(--m-font)', fontSize: 11,
          padding: '6px 10px', borderRadius: 3,
          cursor: 'default', textAlign: 'left',
        }}
      >
        <span>{item.label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {item.hint && <span style={{ color: C.dim, fontSize: 9.5 }}>{item.hint}</span>}
          <span style={{ color: C.dim, fontSize: 10 }}>▸</span>
        </span>
      </button>
      <MenuSubmenu
        rowRef={rowRef}
        open={open}
        minWidth={SUB_W}
        flip={flip}
        onMouseEnter={onOpen}
        style={{
          background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 4, padding: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,.5)',
        }}
      >
        {item.children.map(child => (
          <MenuItem key={child.key} item={child} onClick={() => onPick(child.key)} />
        ))}
      </MenuSubmenu>
    </div>
  )
}

function MenuItem({ item, onClick }) {
  const [hover, setHover] = React.useState(false)
  const disabled = !!item.disabled
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', justifyContent: 'space-between',
        background: hover ? C.hover : 'transparent',
        border: 'none',
        color: disabled ? '#52555f' : (item.danger ? '#fca5a5' : C.text),
        fontFamily: 'var(--m-font)', fontSize: 11,
        padding: '6px 10px', borderRadius: 3,
        cursor: disabled ? 'default' : 'pointer', textAlign: 'left',
      }}
    >
      <span>{item.label}</span>
      {item.hint && <span style={{ color: C.dim, fontSize: 9.5 }}>{item.hint}</span>}
    </button>
  )
}
